import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Avatar
} from '@mui/material';
import { Refresh, Add, Edit, Delete, DragIndicator } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const SanataniCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameHindi: '',
    icon: '',
    description: '',
    color: '#FF9800'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/category`, { headers: getAuthHeaders() });
      if (res.data.success) {
        setCategories(res.data.data || []);
      }
    } catch (err) {
      setError('Error fetching categories: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenAdd = () => {
    setSelectedCategory(null);
    setFormData({ name: '', nameHindi: '', icon: '', description: '', color: '#FF9800' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name || '',
      nameHindi: category.nameHindi || '',
      icon: category.icon || '',
      description: category.description || '',
      color: category.color || '#FF9800'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedCategory) {
        await axios.put(
          `${API_BASE}/admin/categories/${selectedCategory._id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        setSuccess('Category updated successfully');
      } else {
        await axios.post(`${API_BASE}/admin/categories`, formData, { headers: getAuthHeaders() });
        setSuccess('Category created successfully');
      }
      setDialogOpen(false);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error saving category: ' + err.message);
    }
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/admin/categories/${selectedCategory._id}`, { 
        headers: getAuthHeaders() 
      });
      setDeleteDialogOpen(false);
      setSuccess('Category deleted successfully');
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error deleting category: ' + err.message);
    }
  };

  const handleSeedCategories = async () => {
    try {
      await axios.post(`${API_BASE}/admin/categories/seed`, {}, { headers: getAuthHeaders() });
      setSuccess('Default categories seeded successfully');
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error seeding categories: ' + err.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          📂 Sanatani Manch Categories
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            onClick={handleSeedCategories} 
            sx={{ mr: 1 }}
          >
            Seed Default Categories
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleOpenAdd}
            sx={{ bgcolor: '#FF9800', mr: 1 }}
          >
            Add Category
          </Button>
          <IconButton onClick={fetchCategories} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Order</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Icon</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name (Hindi)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Color</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Posts</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category, index) => (
                  <TableRow key={category._id} hover>
                    <TableCell>
                      <IconButton size="small">
                        <DragIndicator />
                      </IconButton>
                      {category.order || index + 1}
                    </TableCell>
                    <TableCell>
                      <Avatar sx={{ bgcolor: category.color || '#FF9800' }}>
                        {category.icon || category.name?.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{category.name}</TableCell>
                    <TableCell>{category.nameHindi || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {category.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        width: 30, 
                        height: 30, 
                        bgcolor: category.color || '#FF9800',
                        borderRadius: 1,
                        border: '1px solid #ccc'
                      }} />
                    </TableCell>
                    <TableCell>{category.postCount || 0}</TableCell>
                    <TableCell>
                      <Chip 
                        label={category.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        color={category.isActive !== false ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleOpenEdit(category)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteClick(category)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No categories found. Click "Seed Default Categories" to add default ones.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          {selectedCategory ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name (English)"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name (Hindi)"
                value={formData.nameHindi}
                onChange={(e) => setFormData({ ...formData, nameHindi: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Icon (Emoji or URL)"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="🙏"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#FF9800' }}>
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#f44336' }}>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{selectedCategory?.name}</b>?
          </Typography>
          <Typography variant="caption" color="error">
            Posts in this category will become uncategorized.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniCategories;
